import React, { useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Wrapper, Header, Footer } from '../../general_components/forms/Form';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { addNotification } from '../../supplementary_components/store';
import styles from './changePasswordPopup.module.scss';

const REQUIREMENTS = [
    { key: 'minLength', label: 'At least 8 characters long', test: (value) => value.length >= 8 },
    { key: 'hasLowerCase', label: 'At least one lowercase letter', test: (value) => /[a-z]/.test(value) },
    { key: 'hasNumber', label: 'At least one number', test: (value) => /[0-9]/.test(value) },
];

const PasswordField = ({ id, label, value, onChange }) => {
    const [visible, setVisible] = useState(false);
    return (
        <div className={styles.field}>
            <label htmlFor={id}>{label}</label>
            <div className={styles.inputWrapper}>
                <input
                    id={id}
                    type={visible ? 'text' : 'password'}
                    value={value}
                    autoComplete={id === 'currentPassword' ? 'current-password' : 'new-password'}
                    onChange={(e) => onChange(e.target.value)}
                />
                <span className={styles.toggle} onClick={() => setVisible((v) => !v)}>
                    {visible ? <FaEyeSlash /> : <FaEye />}
                </span>
            </div>
        </div>
    );
};

export const ChangePasswordPopup = ({ closePopup, popupData }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const onSubmit = () => {
        if (saving) return;
        if (!currentPassword) {
            setError('Please enter your current password.');
            return;
        }
        if (!REQUIREMENTS.every((r) => r.test(newPassword))) {
            setError('Please meet all the password requirements.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords must match.');
            return;
        }
        setError(null);
        setSaving(true);
        sendRequest(
            'POST',
            API_ROOT + 'auth/update',
            null,
            (response) => {
                // Changing the password signs out other sessions and issues new tokens for this one.
                localStorage.setItem('access_token', response.access_token);
                localStorage.setItem('refresh_token', response.refresh_token);
                setSaving(false);
                addNotification({ message: 'Password updated', type: 'success' });
                closePopup();
            },
            (response) => {
                setSaving(false);
                setError(response?.message || 'Failed to update password. Please try again.');
            },
            { login: popupData.login, oldPassword: currentPassword, newPassword }
        );
    };

    return (
        <Wrapper>
            <Header>
                <span>Change Password</span>
            </Header>
            <form
                className={styles.content}
                onSubmit={(e) => {
                    e.preventDefault();
                    onSubmit();
                }}
            >
                <PasswordField id="currentPassword" label="Current Password" value={currentPassword} onChange={setCurrentPassword} />
                <PasswordField id="newPassword" label="New Password" value={newPassword} onChange={setNewPassword} />
                <PasswordField id="confirmPassword" label="Confirm New Password" value={confirmPassword} onChange={setConfirmPassword} />
                <ul className={styles.requirements}>
                    {REQUIREMENTS.map((r) => (
                        <li key={r.key} className={r.test(newPassword) ? styles.met : ''}>{r.label}</li>
                    ))}
                </ul>
                {error ? <p className={styles.error}>{error}</p> : null}
                <p className={styles.hint}>You will stay signed in here; other devices will be signed out.</p>
                <button type="submit" hidden />
            </form>
            <Footer>
                <div className="non-selectable" onClick={() => !saving && closePopup()}>
                    Cancel
                </div>
                <div className={`non-selectable ${saving ? 'disabled' : ''}`} onClick={onSubmit}>
                    <input type="button" value={saving ? 'Saving…' : 'Change Password'} readOnly disabled={saving} />
                </div>
            </Footer>
        </Wrapper>
    );
};
